import { useState } from 'react';
import { t, Trans } from '@lingui/macro';
import { Button, Input, PasswordInput } from '@mantine/core';
import { isNotEmpty, useForm } from '@mantine/form';
import { observer } from 'mobx-react-lite';
import { IMaskInput } from 'react-imask';
import { useMatch, useNavigate } from 'react-router-dom';
import { useTypedSearchParams } from 'react-router-typesafe-routes/dom';
import { Lock, User } from 'tabler-icons-react';

import { apiClient } from '../../core/libs/api';
import { HttpResource } from '../../core/libs/http-resource';
import { AppNotify } from '../../features/app-notify/app-notify';
import { routeCabinet, routeSearch, routeSignIn } from '../../router/routes';
import { authStore } from '../auth/auth.store';
import { useProfileStore } from '../profile/profile-provider';
import { signInRequest } from './requests/sign-in.request';

export const SignInForm = observer(() => {
  const [searchParams] = useTypedSearchParams(routeSearch);
  const [signInParams] = useTypedSearchParams(routeSignIn);

  const navigate = useNavigate();
  const profileStore = useProfileStore();

  const signInRoute = useMatch(routeSignIn);
  const orderTicketsRoute = useMatch(routeSearch);

  const form = useForm({
    initialValues: {
      login: '',
      password: '',
    },

    validate: {
      login: isNotEmpty(t`Пожалуйста, заполните поле`),
      password: isNotEmpty(t`Пожалуйста, заполните поле`),
    },
  });

  const [notify] = useState(() => new AppNotify());

  const handleAuth = (result: any) => {
    if (!result.session) {
      return notify.addMessage({
        type: 'error',
        message: t`Авторизация не удалась`,
      });
    }

    authStore.accessGranted();
    profileStore.setUserData(result);

    const cont = signInParams.continue;

    if (cont === 'order-ticket' || orderTicketsRoute !== null) {
      const lowDate = searchParams.lowDate;
      const startStation = searchParams.startStation;
      const endStation = searchParams.endStation;
      const highDate = searchParams.highDate;

      const path = routeSearch.buildPath(
        {},
        {
          lowDate,
          startStation,
          endStation,
          highDate,
        }
      );

      return navigate(path);
    }

    if (signInRoute === null) {
      window.location.reload();
      return;
    }

    return navigate(routeCabinet.path);
  };

  const [request] = useState(
    () =>
      new HttpResource({
        apiClient,
        httpRequest: signInRequest,
        errorNotify: notify,
        onFinish: (result: any) => {
          handleAuth(result);
        },
      })
  );

  return (
    <form onSubmit={form.onSubmit((values) => request.send(values))}>
      <Input.Wrapper
        label={<Trans>Номер телефона</Trans>}
        size="lg"
        {...form.getInputProps('login')}
      >
        <Input<any>
          size="lg"
          icon={<User size="1rem" />}
          component={IMaskInput}
          placeholder="374 01 234 567"
          mask="{374} 00 000 000"
          value={form.getInputProps('login').value}
          onBlur={form.getInputProps('login').onBlur}
          onFocus={form.getInputProps('login').onFocus}
          error={form.getInputProps('login').error}
          onAccept={(_: any, mask: { unmaskedValue: string }) => {
            form.getInputProps('login').onChange(mask.unmaskedValue);
          }}
        />
      </Input.Wrapper>

      <PasswordInput
        size="lg"
        name="password"
        mt={8}
        label={<Trans>Пароль</Trans>}
        autoComplete="current-password"
        icon={<Lock size="1rem" />}
        {...form.getInputProps('password')}
      />

      <Button
        size="lg"
        mt="xl"
        fullWidth
        type="submit"
        loading={request.isLoading}
      >
        <Trans>Войти</Trans>
      </Button>
    </form>
  );
});
